import fm from 'front-matter'
import { verificationFromAttributes, hasCriticalFinding, type Verification } from './verification'

export type Difficulty = 'easy' | 'medium' | 'hard'
export type QuestionType = 'multiple-choice' | 'free-entry' | 'multi-part'
export type QuizMode = 'quiz' | 'mock-exam'

export interface AnswerOption {
  key: string   // "A", "B", "C", "D"
  text: string
}

export interface Part {
  label: string              // "a", "b", "c", "d", "e"
  points: number
  stem: string
  type: 'multiple-choice' | 'free-entry'
  options: AnswerOption[]    // empty for free-entry
  answer: string             // correct answer value
  explanation: string
  examiner_report?: string
}

export interface Question {
  id: string
  exam: string
  topic: string
  learning_objective: string  // syllabus learning-objective name, e.g. "Univariate Random Variables"
  difficulty: Difficulty
  type: QuestionType
  wiki_link: string[]  // normalized to array; single-string frontmatter becomes [string]
  answer: string       // '' for multi-part (answers live in parts[])
  explanation: string  // '' for multi-part
  points: number
  stem: string         // question body text; shared preamble for multi-part
  options: AnswerOption[]  // [] for free-entry and multi-part
  parts?: Part[]           // populated for multi-part questions
  examiner_report?: string // examiner's notes for single-part question types
  author?: string
  year?: number
  session?: string  // e.g. "Spring" or "Fall" for CAS/SOA sittings
  /**
   * The exam this question was originally sat under, when the syllabus has
   * since moved the material to the exam named in `exam`. Set on the 2018
   * MAS-I sittings whose content CAS later moved to MAS-II.
   */
  originally_exam?: string
  /**
   * Set on a question from a part of an old syllabus that no current exam
   * covers — the insurance-company valuation questions on the 2012–2019
   * Exam 7 papers. Kept for the record, not for study: `filterQuestions`
   * leaves it out of quiz draws unless a reader asks for its sitting, for it by
   * id, or searches for it.
   */
  off_syllabus?: boolean
  /**
   * The question's VERIFY record — what has been checked about it, against what
   * source, and when. Undefined only for a file with no `verification:` block,
   * which CI does not allow into the bank.
   */
  verification?: Verification
}

export interface QuestionFilter {
  exam?: string
  topic?: string
  topics?: string[]  // multi-select topic filter
  learningObjective?: string
  learningObjectives?: string[]  // multi-select learning-objective filter
  difficulty?: Difficulty
  /** Keep only these levels — the quiz builder's difficulty multi-select. Empty or absent filters nothing. */
  difficulties?: Difficulty[]
  mode?: QuizMode
  count?: number        // max questions to return
  /**
   * A lean toward a difficulty (0 Easy … 1 Hard) — what links made under the
   * quiz builder's old slider carry. Not a filter — `filterQuestions` ignores
   * it — but a lean on which `count` questions are drawn. See lib/quizDifficulty.ts.
   */
  difficultyTarget?: number
  author?: string       // partial match, case-insensitive
  year?: number
  session?: string      // filter by sitting session, e.g. "Spring" or "Fall"
  search?: string       // free-text search on stem and id
  ids?: string[]        // when set, only return questions whose id is in this list
  concept?: string      // filter by wiki_link concept name (single)
  concepts?: string[]   // filter by wiki_link concept names (multi-select)
  /**
   * Include questions the fact-check record flags as critically wrong. Off by
   * default — see `hasCriticalFinding`. Review surfaces turn it on so the
   * flagged questions can actually be fixed.
   */
  includeFlagged?: boolean
  /**
   * Include questions no current syllabus covers (`off_syllabus`) even with no
   * sitting, id or search to ask for them — for surfaces that list the bank
   * rather than draw a quiz from it.
   */
  includeOffSyllabus?: boolean
}

interface QuestionFrontmatter {
  id?: unknown
  exam?: unknown
  topic?: unknown
  learning_objective?: unknown
  difficulty?: unknown
  type?: unknown
  wiki_link?: unknown
  answer?: unknown
  explanation?: unknown  // kept for backward compat
  points?: unknown
  author?: unknown
  year?: unknown
  session?: unknown
  originally_exam?: unknown
  off_syllabus?: unknown
  verification?: unknown
}

const OPTION_REGEX = /^- ([A-E])\)\s+(.+)/

export type SelfGrade = 'correct' | 'partial' | 'incorrect'

// Normalize a free-entry answer for comparison: strip whitespace and common
// currency/formatting characters ($, €, £, commas), then if parseable as a
// number round to 4 decimal places so "3400", "$3,400", and "3,400" all compare
// equal, and "3.670" and "3.67" compare equal.
export function normalizeAnswerText(s: string): string {
  const trimmed = s.trim()
  const stripped = trimmed.replace(/[$€£,]/g, '')
  const num = Number(stripped)
  if (stripped !== '' && !isNaN(num)) {
    return String(Math.round(num * 10000) / 10000)
  }
  return trimmed.toLowerCase()
}

// Single grading function that works for all question types.
export function isAnswerCorrect(question: Question, chosen: string): boolean {
  if (question.type === 'multiple-choice') {
    return chosen === question.answer
  }
  if (question.type === 'free-entry') {
    return normalizeAnswerText(chosen) === normalizeAnswerText(question.answer)
  }
  if (question.type === 'multi-part') {
    try {
      const chosenParts = JSON.parse(chosen) as Record<string, string>
      const parts = question.parts ?? []
      if (parts.length === 0) return false
      // Written parts (answer === '') have no key: only the reader's self-grade
      // can mark them (questionCredit), so with no grades they are left out — and
      // a question that is nothing but written parts is not correct by default.
      const gradedParts = parts.filter(p => p.answer !== '')
      if (gradedParts.length === 0) return false
      return gradedParts.every(part => {
        const partChosen = chosenParts[part.label] ?? ''
        if (part.type === 'multiple-choice') return partChosen === part.answer
        return normalizeAnswerText(partChosen) === normalizeAnswerText(part.answer)
      })
    } catch {
      return false
    }
  }
  return false
}

export type QuestionOutcome = 'correct' | 'partial' | 'incorrect'

// Fraction of credit [0,1] earned on a question — multi-part parts weighted by
// their points — honouring manual grade
// overrides and — crucially — awarding *partial* credit for multi-part
// questions where only some graded parts are right (or a part was manually
// graded 'partial'). `isAnswerCorrect` collapses these to a single boolean;
// this is the graded-per-part view used by the results summary.
export function questionCredit(
  question: Question,
  chosen: string | null | undefined,
  manualGrades: Record<string, SelfGrade> = {},
): number {
  if (chosen == null) return 0
  if (question.type === 'multiple-choice') {
    return isAnswerCorrect(question, chosen) ? 1 : 0
  }
  if (question.type === 'free-entry') {
    const override = manualGrades[question.id]
    if (override !== undefined) return override === 'correct' ? 1 : override === 'partial' ? 0.5 : 0
    return isAnswerCorrect(question, chosen) ? 1 : 0
  }
  if (question.type === 'multi-part') {
    try {
      const parts = question.parts ?? []
      const chosenParts = JSON.parse(chosen) as Record<string, string>
      // Each part is weighted by the points the paper gives it, so a 2-point
      // part counts eight times a 0.25-point one. A question whose parts don't
      // all carry points weighs them equally rather than zeroing the bare ones.
      const weighted = parts.every(p => p.points > 0)
      let earned = 0
      let graded = 0
      for (const part of parts) {
        const weight = weighted ? part.points : 1
        const override = manualGrades[`${question.id}__${part.label}`]
        if (override !== undefined) {
          graded += weight
          earned += weight * (override === 'correct' ? 1 : override === 'partial' ? 0.5 : 0)
          continue
        }
        // A written part (answer === '') counts only once the reader has
        // self-graded it; left ungraded, it is out of the tally.
        if (part.answer === '') continue
        graded += weight
        const partChosen = chosenParts[part.label] ?? ''
        const right = part.type === 'multiple-choice'
          ? partChosen === part.answer
          : normalizeAnswerText(partChosen) === normalizeAnswerText(part.answer)
        earned += right ? weight : 0
      }
      // Nothing graded (all written parts, none self-graded) earns nothing.
      return graded > 0 ? earned / graded : 0
    } catch {
      return 0
    }
  }
  return 0
}

// Three-state outcome derived from questionCredit: full credit → 'correct',
// no credit → 'incorrect', anything in between → 'partial' (shown yellow).
export function questionOutcome(
  question: Question,
  chosen: string | null | undefined,
  manualGrades: Record<string, SelfGrade> = {},
): QuestionOutcome {
  const credit = questionCredit(question, chosen, manualGrades)
  if (credit >= 1) return 'correct'
  if (credit <= 0) return 'incorrect'
  return 'partial'
}

// Whether every graded part of a multi-part question has a non-empty answer
// (essay parts with answer === '' need no user input and are skipped).
export function isMultiPartAnswerComplete(question: Question, chosen: string): boolean {
  const parts = question.parts ?? []
  if (parts.length === 0) return true
  try {
    const chosenParts = JSON.parse(chosen) as Record<string, string>
    return parts.every(p => p.answer === '' || (chosenParts[p.label] ?? '').trim() !== '')
  } catch {
    return false
  }
}

// Parse the content within a single Part block into its sub-sections.
function parsePartSections(content: string): {
  stem: string
  answer: string
  explanation: string
  examiner_report?: string
  options: AnswerOption[]
} {
  const sectionBlocks = content.split(/^### /m)
  const rawStem = sectionBlocks[0].trim()

  let answer = ''
  let explanation = ''
  let examiner_report: string | undefined

  for (let i = 1; i < sectionBlocks.length; i++) {
    const block = sectionBlocks[i]
    const newlineIdx = block.indexOf('\n')
    const heading = (newlineIdx >= 0 ? block.slice(0, newlineIdx) : block).trim().toLowerCase()
    const body = (newlineIdx >= 0 ? block.slice(newlineIdx + 1) : '').trim()

    if (heading === 'answer') {
      answer = body.split('\n')[0].trim()
    } else if (heading === 'explanation') {
      explanation = body
    } else if (heading === 'examiner report') {
      examiner_report = body
    }
  }

  // Detect option lines within the stem (supports mixed MC/free-entry multi-part)
  const stemLines = rawStem.split('\n')
  const optionStartIdx = stemLines.findIndex(l => OPTION_REGEX.test(l))
  let stem = rawStem
  let options: AnswerOption[] = []

  if (optionStartIdx >= 0) {
    stem = stemLines.slice(0, optionStartIdx).join('\n').trim()
    options = stemLines
      .slice(optionStartIdx)
      .filter(l => OPTION_REGEX.test(l))
      .map(l => {
        const match = l.match(OPTION_REGEX)!
        return { key: match[1], text: match[2].trim() }
      })
  }

  return { stem, answer, explanation, examiner_report, options }
}

// Parse the body of a multi-part question into an array of Parts.
// Format:
//   ## Part a (0.25 points)
//   [stem text]
//   ### Answer
//   4.5
//   ### Explanation
//   [text]
//   ### Examiner Report
//   [text]
function parseMultiPartBody(body: string): Part[] | null {
  const PART_HEADING_RE = /^## Part ([A-Ha-h])(?:\s*\(([0-9.]+)\s*points?\))?[^\n]*/gm
  const matches = [...body.matchAll(PART_HEADING_RE)]
  if (matches.length === 0) return null

  const parts: Part[] = []
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i]
    const label = match[1].toLowerCase()
    const points = match[2] ? parseFloat(match[2]) : 0

    const startIdx = match.index! + match[0].length
    const endIdx = i + 1 < matches.length ? matches[i + 1].index! : body.length
    const partContent = body.slice(startIdx, endIdx).trim()

    const { stem, answer, explanation, examiner_report, options } = parsePartSections(partContent)
    const type: 'multiple-choice' | 'free-entry' = options.length > 0 ? 'multiple-choice' : 'free-entry'

    // Skip malformed MC parts where the answer key has no matching option
    if (type === 'multiple-choice' && answer && !options.some(o => o.key === answer)) {
      continue
    }

    parts.push({ label, points, stem, type, options, answer, explanation, examiner_report })
  }

  return parts.length > 0 ? parts : null
}

export function parseQuestion(raw: string): Question | null {
  try {
    const parsed = fm<QuestionFrontmatter>(raw)
    const data = parsed.attributes
    const content = parsed.body
    const type = data.type as QuestionType

    if (!data.id || !data.exam || !data.topic || !data.difficulty || !data.type) {
      return null
    }
    // multi-part stores answers in parts[], not in frontmatter
    if (type !== 'multi-part' && !data.answer) {
      return null
    }

    const rawLink = data.wiki_link
    const wikiLinks: string[] = Array.isArray(rawLink)
      ? (rawLink as unknown[]).map(String).filter(s => s.length > 0)
      : rawLink ? [String(rawLink)].filter(s => s.length > 0) : []

    const base = {
      id: String(data.id),
      exam: String(data.exam),
      topic: String(data.topic),
      learning_objective: data.learning_objective ? String(data.learning_objective) : '',
      difficulty: data.difficulty as Difficulty,
      type,
      wiki_link: wikiLinks,
      points: Number(data.points ?? 1),
      author: data.author ? String(data.author) : undefined,
      year: data.year ? Number(data.year) : undefined,
      session: data.session ? String(data.session) : undefined,
      originally_exam: data.originally_exam ? String(data.originally_exam) : undefined,
      off_syllabus: String(data.off_syllabus).toLowerCase() === 'true' ? true : undefined,
      verification: verificationFromAttributes(data) ?? undefined,
    }

    // ── multi-part ──────────────────────────────────────────────────────────
    if (type === 'multi-part') {
      const firstPartIdx = content.search(/^## Part /m)
      const parts = parseMultiPartBody(content)
      if (parts) {
        const stem = firstPartIdx >= 0
          ? content.slice(0, firstPartIdx).trim()
          : content.trim()
        return { ...base, stem, options: [], answer: '', explanation: '', parts }
      }

      // No explicit "## Part" headings — treat the whole body as a single
      // implicit part. Covers single-part CAS written/calculation questions
      // authored with ### Answer/### Explanation/### Examiner Report sections
      // but no part split, so they parse instead of being silently dropped.
      const sec = parsePartSections(content)
      if (!sec.answer && !sec.explanation) return null
      const singlePart: Part = {
        label: 'a',
        points: base.points,
        stem: '',
        type: sec.options.length > 0 ? 'multiple-choice' : 'free-entry',
        options: sec.options,
        answer: sec.answer,
        explanation: sec.explanation,
        examiner_report: sec.examiner_report,
      }
      return { ...base, stem: sec.stem, options: [], answer: '', explanation: '', parts: [singlePart] }
    }

    // ── free-entry and multiple-choice ──────────────────────────────────────
    // Split off "## Explanation" section
    const bodyParts = content.split(/^## Explanation\s*$/m)
    const bodyBeforeExplanation = bodyParts[0]
    const rawExplanation = bodyParts.length > 1 ? bodyParts[1].trim() : null

    // Split off "## Examiner Report" from whichever section it appears in
    let explanation: string
    let examiner_report: string | undefined

    if (rawExplanation !== null) {
      const expParts = rawExplanation.split(/^## Examiner Report\s*$/m)
      explanation = expParts[0].trim()
      examiner_report = expParts.length > 1 ? expParts[1].trim() : undefined
    } else {
      // Backward compat: explanation in YAML frontmatter
      explanation = String(data.explanation ?? '')
    }

    // Also check body-before-explanation for an Examiner Report block
    if (!examiner_report) {
      const bodyExParts = bodyBeforeExplanation.split(/^## Examiner Report\s*$/m)
      if (bodyExParts.length > 1) {
        examiner_report = bodyExParts[1].split(/^## /m)[0].trim() || undefined
      }
    }

    // ── free-entry ──────────────────────────────────────────────────────────
    if (type === 'free-entry') {
      const stemContent = bodyBeforeExplanation
        .split(/^## Examiner Report\s*$/m)[0]
        .trim()

      return {
        ...base,
        stem: stemContent,
        options: [],
        answer: String(data.answer),
        explanation,
        examiner_report,
      }
    }

    // ── multiple-choice ─────────────────────────────────────────────────────
    const lines = bodyBeforeExplanation.trim().split('\n')
    const optionStartIdx = lines.findIndex(l => OPTION_REGEX.test(l))

    const stem = optionStartIdx > 0
      ? lines.slice(0, optionStartIdx).join('\n').trim()
      : bodyBeforeExplanation.trim()

    const options: AnswerOption[] = optionStartIdx >= 0
      ? lines
          .slice(optionStartIdx)
          .filter(l => OPTION_REGEX.test(l))
          .map(l => {
            const match = l.match(OPTION_REGEX)!
            return { key: match[1], text: match[2].trim() }
          })
      : []

    const answerKey = String(data.answer)
    if (options.length === 0 || !options.some(o => o.key === answerKey)) {
      return null
    }

    return {
      ...base,
      stem,
      options,
      answer: answerKey,
      explanation,
      examiner_report,
    }
  } catch {
    return null
  }
}

const STOP_WORDS = new Set([
  'the', 'and', 'that', 'this', 'with', 'for', 'are', 'was', 'were', 'been',
  'have', 'has', 'had', 'will', 'would', 'could', 'should', 'may', 'might',
  'shall', 'can', 'does', 'did', 'not', 'but', 'from', 'they', 'their',
  'there', 'when', 'where', 'which', 'who', 'what', 'how', 'its', 'also',
  'more', 'than', 'some', 'such', 'each', 'into', 'about', 'over', 'does',
  'only', 'very', 'even', 'most', 'both', 'your', 'our', 'any', 'all',
])

export function estimateEssayScore(
  userAnswer: string,
  sampleAnswer: string,
): { matched: number; total: number; pct: number } {
  if (!userAnswer.trim() || !sampleAnswer.trim()) return { matched: 0, total: 0, pct: 0 }

  const tokenize = (text: string) =>
    new Set(
      text
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 3 && !STOP_WORDS.has(w)),
    )

  const sampleWords = tokenize(sampleAnswer)
  if (sampleWords.size === 0) return { matched: 0, total: 0, pct: 0 }

  const userWords = tokenize(userAnswer)
  let matched = 0
  for (const w of sampleWords) {
    if (userWords.has(w)) matched++
  }

  return { matched, total: sampleWords.size, pct: Math.round((matched / sampleWords.size) * 100) }
}

export function parseAllQuestions(rawFiles: string[]): Question[] {
  const seen = new Set<string>()
  const result: Question[] = []
  for (const raw of rawFiles) {
    const q = parseQuestion(raw)
    if (!q) continue
    if (seen.has(q.id)) continue
    seen.add(q.id)
    result.push(q)
  }
  return result
}

/**
 * True when `q` sits on `exam` only because the syllabus moved — it was written
 * for, and sat on, the paper of a different exam (`originally_exam`).
 *
 * Such a question keeps the `year`/`session` of the paper it really came from,
 * so those dates name a sitting of the *other* exam. Anything that reasons
 * about "this exam's sitting" has to skip it, or the transplanted date invents
 * a sitting that never happened — the CAS moved Time Series and Statistical
 * Learning from MAS-I to MAS-II, and the re-tagged MAS-I Spring 2018 questions
 * would otherwise conjure an "Exam MAS-II Spring 2018" paper, six months before
 * MAS-II was first sat.
 */
export function isFromAnotherExamsPaper(q: Question, exam: string): boolean {
  const from = q.originally_exam?.trim()
  return !!from && from.toLowerCase() !== exam.trim().toLowerCase()
}

/**
 * How a question's `learning_objective` is matched to a syllabus section. The
 * CAS content outlines letter their domains (`A. Ratemaking`) and the exam page
 * keeps the letter; a question names the domain by its words. So the letter is
 * dropped, with case and spacing, before comparing — `A. Ratemaking`,
 * `ratemaking` and `Ratemaking` are one objective. Mirrors `objective_key` in
 * scripts/syllabus_lib.py, which is what scripts/syllabus_lint.py holds every
 * bank to.
 */
export function objectiveKey(title: string): string {
  return title.trim().replace(/^[A-Z]\.\s+/, '').replace(/\s+/g, ' ').toLowerCase()
}

export function filterQuestions(questions: Question[], filters: QuestionFilter): Question[] {
  return questions.filter(q => {
    // Before anything else, and ahead of the `ids` short-circuit: a question the
    // record says is critically wrong should not reach a student by any route,
    // including a direct id lookup, unless the caller has explicitly asked for
    // flagged questions.
    if (!filters.includeFlagged && hasCriticalFinding(q.verification)) return false
    if (filters.ids?.length) return filters.ids.includes(q.id)
    // A question kept only for the record: it belongs to its paper, so a
    // sitting filter still finds it, and so does a search — but a quiz drawn
    // from an exam, a topic or a concept never serves it.
    if (q.off_syllabus && !filters.includeOffSyllabus && !filters.year && !filters.session && !filters.search) return false
    if (filters.exam && q.exam.toLowerCase() !== filters.exam.toLowerCase()) return false
    if (filters.topic && q.topic.toLowerCase() !== filters.topic.toLowerCase()) return false
    if (filters.topics?.length) {
      if (!filters.topics.some(s => q.topic.toLowerCase() === s.toLowerCase())) return false
    }
    if (filters.learningObjective && objectiveKey(q.learning_objective) !== objectiveKey(filters.learningObjective)) return false
    if (filters.learningObjectives?.length) {
      const key = objectiveKey(q.learning_objective)
      if (!filters.learningObjectives.some(s => key === objectiveKey(s))) return false
    }
    if (filters.difficulty && q.difficulty !== filters.difficulty) return false
    if (filters.difficulties?.length && !filters.difficulties.includes(q.difficulty)) return false
    if (filters.author) {
      if (!q.author?.toLowerCase().includes(filters.author.toLowerCase())) return false
    }
    if (filters.year && q.year !== filters.year) return false
    if (filters.session && q.session?.toLowerCase() !== filters.session.toLowerCase()) return false
    // Filtering to a sitting of a named exam: a question carried over from
    // another exam's paper is not part of it, whatever date it still carries.
    if ((filters.year || filters.session) && filters.exam && isFromAnotherExamsPaper(q, filters.exam)) return false
    if (filters.search) {
      const needle = filters.search.toLowerCase()
      if (!q.stem.toLowerCase().includes(needle) && !q.id.toLowerCase().includes(needle)) return false
    }
    if (filters.concept) {
      const needle = filters.concept.toLowerCase()
      const matches = q.wiki_link.some(link => {
        const clean = link.replace(/\+/g, ' ').replace(/\.md$/i, '')
        const lastSegment = clean.split('/').filter(Boolean).pop() ?? ''
        return lastSegment.toLowerCase() === needle
      })
      if (!matches) return false
    }
    if (filters.concepts?.length) {
      const needles = new Set(filters.concepts.map(c => c.toLowerCase()))
      const matches = q.wiki_link.some(link => {
        const clean = link.replace(/\+/g, ' ').replace(/\.md$/i, '')
        const lastSegment = clean.split('/').filter(Boolean).pop() ?? ''
        return needles.has(lastSegment.toLowerCase())
      })
      if (!matches) return false
    }
    return true
  })
}
